import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { MapPin, Mail, Phone, ArrowRight, Loader2, CalendarIcon, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const CONTACT_IMG =
  "https://images.unsplash.com/photo-1584385971010-71c147ba5dbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxJbmRpYW5hcG9saXMlMjBza3lsaW5lJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MzM3NDExNXww&ixlib=rb-4.1.0&q=85";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "9:00 AM ET",
  "10:00 AM ET",
  "11:00 AM ET",
  "12:00 PM ET",
  "1:00 PM ET",
  "2:00 PM ET",
  "3:00 PM ET",
  "4:00 PM ET",
  "5:00 PM ET",
];

const initial = {
  name: "",
  email: "",
  company: "",
  service: "",
  message: "",
  preferred_date: null, // Date object
  preferred_time: "",
};

export default function LuxeContact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "A valid email is required";
    if (!form.message.trim() || form.message.trim().length < 10)
      return "Please share a bit more detail (10+ characters)";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
        preferred_date: form.preferred_date
          ? format(form.preferred_date, "yyyy-MM-dd")
          : null,
        preferred_time: form.preferred_time || null,
      };
      await axios.post(`${API}/contact`, payload);
      const scheduling = form.preferred_date || form.preferred_time;
      toast.success(
        scheduling
          ? "Discovery call request received. We'll confirm your time within one business day."
          : "Your message is on its way. We'll be in touch shortly."
      );
      setForm(initial);
    } catch (e2) {
      console.error(e2);
      const detail = e2?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="luxe-contact">
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 md:pt-24 pb-16">
        <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-6">
          Begin a conversation
        </div>
        <h1 className="luxe-hero font-serif-display text-[#0a1f5c] font-medium max-w-5xl">
          Say <em className="italic text-[#b8964f]">hello.</em>
          <br />
          Let's build together.
        </h1>
        <p className="mt-8 max-w-2xl text-lg md:text-xl font-fraunces text-[#3a3a37] leading-relaxed">
          Tell us a little about your business and what you're trying to solve.
          We respond within one business day.
        </p>
      </section>

      <section className="border-t border-[#e6dbc7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Left: info */}
          <div className="md:col-span-5">
            <div className="gold-frame mb-14">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={CONTACT_IMG}
                  alt="Indianapolis skyline"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="space-y-10">
              <div>
                <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-3">
                  Studio
                </div>
                <div className="flex gap-4 items-start">
                  <MapPin className="w-5 h-5 mt-1.5 text-[#b8964f] shrink-0" />
                  <p
                    data-testid="luxe-contact-address"
                    className="font-serif-display text-xl md:text-2xl text-[#0a1f5c] leading-snug"
                  >
                    320 N Meridian St
                    <br />
                    Indianapolis, IN 46204
                  </p>
                </div>
              </div>
              <div>
                <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-3">
                  Email
                </div>
                <div className="flex gap-4 items-start">
                  <Mail className="w-5 h-5 mt-1.5 text-[#b8964f] shrink-0" />
                  <a
                    href="mailto:info@mkabusinesssolutions.com"
                    data-testid="luxe-contact-email-link"
                    className="font-serif-display text-xl md:text-2xl text-[#0a1f5c] luxe-underline"
                  >
                    info@mkabusinesssolutions.com
                  </a>
                </div>
              </div>
              <div>
                <div className="font-fraunces italic text-[#b8964f] text-sm tracking-widest uppercase mb-3">
                  Phone
                </div>
                <div className="flex gap-4 items-start">
                  <Phone className="w-5 h-5 mt-1.5 text-[#b8964f] shrink-0" />
                  <p
                    data-testid="luxe-contact-phone"
                    className="font-serif-display text-xl md:text-2xl text-[#0a1f5c]"
                  >
                    +1 (317) 342-2300
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <form
            onSubmit={onSubmit}
            data-testid="luxe-contact-form"
            className="md:col-span-7 bg-[#fbf8f3] border border-[#e6dbc7] p-8 md:p-12 shadow-sm"
          >
            <div className="font-fraunces italic text-[#b8964f] text-sm mb-2 tracking-wide">
              — For considered inquiries
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#0a1f5c] mb-10">
              Tell us about your project.
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <Label htmlFor="l-name" className="font-fraunces italic text-sm text-[#6b6558]">Your name*</Label>
                <Input
                  id="l-name"
                  data-testid="luxe-input-name"
                  value={form.name}
                  onChange={handleChange("name")}
                  placeholder="Alex Morgan"
                  className="h-12 rounded-none bg-transparent border-0 border-b border-[#c9a46b]/40 focus-visible:ring-0 focus-visible:border-[#0a1f5c] font-serif-display text-lg text-[#0a1f5c] px-0"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="l-email" className="font-fraunces italic text-sm text-[#6b6558]">Email*</Label>
                <Input
                  id="l-email"
                  type="email"
                  data-testid="luxe-input-email"
                  value={form.email}
                  onChange={handleChange("email")}
                  placeholder="you@company.com"
                  className="h-12 rounded-none bg-transparent border-0 border-b border-[#c9a46b]/40 focus-visible:ring-0 focus-visible:border-[#0a1f5c] font-serif-display text-lg text-[#0a1f5c] px-0"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="l-company" className="font-fraunces italic text-sm text-[#6b6558]">Company</Label>
                <Input
                  id="l-company"
                  data-testid="luxe-input-company"
                  value={form.company}
                  onChange={handleChange("company")}
                  placeholder="Acme Inc."
                  className="h-12 rounded-none bg-transparent border-0 border-b border-[#c9a46b]/40 focus-visible:ring-0 focus-visible:border-[#0a1f5c] font-serif-display text-lg text-[#0a1f5c] px-0"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="l-service" className="font-fraunces italic text-sm text-[#6b6558]">Service of interest</Label>
                <Select value={form.service} onValueChange={handleChange("service")}>
                  <SelectTrigger
                    id="l-service"
                    data-testid="luxe-select-service"
                    className="h-12 rounded-none bg-transparent border-0 border-b border-[#c9a46b]/40 focus:ring-0 focus:border-[#0a1f5c] font-serif-display text-lg text-[#0a1f5c] px-0"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Business Consulting">Business Consulting</SelectItem>
                    <SelectItem value="AI Strategy, Training & Consulting">AI Strategy, Training & Consulting</SelectItem>
                    <SelectItem value="Marketing & Branding">Marketing & Branding</SelectItem>
                    <SelectItem value="Custom Solutions">Custom Solutions</SelectItem>
                    <SelectItem value="Not sure">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2 mt-8">
              <Label htmlFor="l-message" className="font-fraunces italic text-sm text-[#6b6558]">
                What are you trying to solve?*
              </Label>
              <Textarea
                id="l-message"
                data-testid="luxe-input-message"
                value={form.message}
                onChange={handleChange("message")}
                rows={6}
                placeholder="Share a bit about your business, timeline, and goals…"
                className="rounded-none bg-transparent border-0 border-b border-[#c9a46b]/40 focus-visible:ring-0 focus-visible:border-[#0a1f5c] font-serif-display text-lg text-[#0a1f5c] px-0"
              />
            </div>

            {/* Discovery Call - optional */}
            <div className="mt-10 border-l-2 border-[#b8964f] pl-6 py-4 bg-[#f0e8d8]/50" data-testid="discovery-call-section">
              <div className="font-fraunces italic text-[#b8964f] text-xs tracking-widest uppercase mb-2">
                Optional
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#0a1f5c] leading-snug mb-2">
                Prefer a <em className="italic text-[#b8964f]">discovery call?</em>
              </h3>
              <p className="font-fraunces text-[#6b6558] text-sm mb-6">
                Suggest a date and time — we'll confirm within one business day.
                Complimentary, 30-minute conversation.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <Label className="font-fraunces italic text-sm text-[#6b6558]">
                    Preferred date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="luxe-date-picker-trigger"
                        className={`h-12 flex items-center justify-between gap-2 rounded-none bg-transparent border-0 border-b border-[#c9a46b]/40 hover:border-[#0a1f5c] font-serif-display text-lg px-0 transition-colors ${
                          form.preferred_date ? "text-[#0a1f5c]" : "text-[#6b6558]/70"
                        }`}
                      >
                        <span>
                          {form.preferred_date
                            ? format(form.preferred_date, "PPP")
                            : "Select a date"}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-[#b8964f]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-[#fbf8f3] border-[#e6dbc7]"
                      align="start"
                      data-testid="luxe-date-picker-content"
                    >
                      <Calendar
                        mode="single"
                        selected={form.preferred_date}
                        onSelect={(d) =>
                          setForm((f) => ({ ...f, preferred_date: d || null }))
                        }
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const day = date.getDay();
                          return date < today || day === 0 || day === 6;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="l-time" className="font-fraunces italic text-sm text-[#6b6558]">
                    Preferred time
                  </Label>
                  <Select
                    value={form.preferred_time}
                    onValueChange={handleChange("preferred_time")}
                  >
                    <SelectTrigger
                      id="l-time"
                      data-testid="luxe-time-select"
                      className="h-12 rounded-none bg-transparent border-0 border-b border-[#c9a46b]/40 focus:ring-0 focus:border-[#0a1f5c] font-serif-display text-lg text-[#0a1f5c] px-0"
                    >
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`time-slot-${t}`}>
                          <span className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-[#b8964f]" />
                            {t}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(form.preferred_date || form.preferred_time) && (
                <button
                  type="button"
                  data-testid="luxe-clear-schedule"
                  onClick={() =>
                    setForm((f) => ({ ...f, preferred_date: null, preferred_time: "" }))
                  }
                  className="mt-4 font-fraunces italic text-xs text-[#b8964f] luxe-underline"
                >
                  Clear scheduling preference
                </button>
              )}
            </div>

            <button
              type="submit"
              data-testid="luxe-form-submit"
              disabled={loading}
              className="mt-10 inline-flex items-center gap-3 bg-[#0a1f5c] text-[#f7f2ea] px-10 py-5 rounded-full font-fraunces text-base hover:bg-[#b8964f] hover:text-[#0a1f5c] transition-all disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send message <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="mt-6 font-fraunces italic text-xs text-[#6b6558]">
              By submitting, you agree to be contacted by MKA Business Solutions
              at the email address you provide. We won't share your info.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
